import React, { useContext, type FC, type PropsWithChildren } from 'react';

import { useProductUrls, isAbsolutePath } from '@lucid/utils';

import type { Lucid, CustomDrupal, Beacon } from '../../types/types';
import LayoutContext from '../context/layout-context';
import Image from '../image/image';
import Link, { type LinkProps } from '../link/link';
import type { SharedNavOptions } from './nav';
import Text from '../text/text';

export interface NavLinkProps extends LinkProps, SharedNavOptions {
  /** Checks if the link is inside the dropdown menu. */
  isInDropdown?: boolean;
  /** Checks if the link has a visio URL param. */
  isVisio?: boolean;
  /** The specific link data. */
  link: CustomDrupal.LinkInterface;
  /** The current site. */
  product: Lucid.Product.Sites;
  /** Allows to remove link from tabindex */
  shouldRemoveFromTabIndex?: boolean;
}

const NavLink: FC<PropsWithChildren<NavLinkProps>> = ({
  className,
  isInDropdown = false,
  isSuperNav = false,
  isVisio = false,
  link,
  product,
  shouldRemoveFromTabIndex = false,
  variant = 'subNavLink',
  ...rest
}) => {
  const { language } = useContext(LayoutContext);
  const { shortLangPrefix, urlLangPrefix } = language;
  const { productUrl } = useProductUrls(urlLangPrefix);
  const isProductSuite = link.entityBundle === 'product-suite';
  const visioQueryParams = '?type=visio';
  let url = link.link?.url!.path;

  url = isVisio && link.fieldKey === 'pricing' ? `${url}${visioQueryParams}` : url;

  if (productUrl && link.fieldProductLink) {
    url = `${productUrl}${shortLangPrefix !== 'en' ? `/${shortLangPrefix}` : ''}${url}`;
  }

  if (link.entityBundle === 'top-navigation' && link.isEnabled === false) {
    // use '=== false' instead of '!link.enabled'
    // since this is used across all sites, err on the side of rendering the item
    // in case "enabled" is removed from the query
    return null;
  }

  if (url?.includes('lucidchart.zendesk.com') && shortLangPrefix !== 'en') {
    url = url.replace('/en-us', `/${shortLangPrefix}`);
  }

  if (link.fieldIsExternalSourceLink) {
    url = link.fieldIsExternalSourceLink;
  }

  if (isSuperNav && url) {
    // checks if the top nav url is relative or absolute
    // if it is relative, it adds the lucid.co domain to it.
    if (!isAbsolutePath(url)) {
      url = `https://lucid.co${url}`;
    }
  }

  const linkTitle = link.fieldIsExternalSourceLinkTi ? link.fieldIsExternalSourceLinkTi : link.title;
  const hasImg = link?.fieldProductLogoImage?.entity.fieldImageLink;

  return (
    <Link
      {...rest}
      beaconEvent={
        {
          name: 'clickedMarketingPageElement',
          elementLocation: isInDropdown ? 'nav-sub' : 'nav',
          elementDetail: linkTitle,
          elementType: 'link',
        } as Beacon.Event
      }
      css={theme => [
        isInDropdown && {
          label: 'subNavLink',
          maxWidth: '100%',
        },
        !isInDropdown && [
          {
            label: 'navLink',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          },
        ],
        isSuperNav && [
          hasImg && {
            '& > img': {
              marginTop: '12px',
            },
            '& > p': {
              marginTop: '12px',
              fontSize: theme.fontSizes.fs12,
            },
            flexDirection: 'column',
            height: 'unset',
            alignItems: 'unset',
            outlineWidth: '2px',
            outlineOffset: '-2px',
            '&:active, &:hover, &:focus': {
              color: theme.colors.coolGrey[80],
              backgroundColor: theme.colors.coolGrey[5],
              '& > p': {
                color: theme.colors.coolGrey[100],
                fontWeight: theme.fontWeights.slightlyStrong,
              },
            },
            '&:focus': {
              outlineColor: theme.colors.coolGrey[60],
            },
          },
          {
            '&:active, &:hover, &:focus': {
              color: theme.colors.coolGrey[80],
              backgroundColor: theme.colors.coolGrey[5],
            },
            '&:focus': {
              outlineColor: theme.colors.blue[60],
            },
          },
        ],
      ]}
      // eslint-disable-next-line react/jsx-sort-props
      className={className}
      displayAsButton={link.isButton || !isInDropdown}
      excludeFromGatsby={link.fieldProductLink}
      tabIndex={shouldRemoveFromTabIndex ? -1 : 0}
      to={url}
      variant={variant}
      willAddBeaconParams={isSuperNav}
    >
      {isProductSuite && link?.fieldProductLogoImage?.entity.fieldImageLink ? (
        <Image
          css={{
            height: '23px',
            width: '154px',
          }}
          dataElementPageLocation={isSuperNav ? 'superNav' : 'siteNav'}
          imageAlt={link?.fieldProductLogoImage?.entity.fieldAltText || ''}
          imageSrc={link?.fieldProductLogoImage?.entity.fieldImageLink}
        />
      ) : isInDropdown ? (
        <Text as="span" dataElementPageLocation={isSuperNav ? 'superNav' : 'siteNav'}>
          {linkTitle}
        </Text>
      ) : (
        linkTitle
      )}
      {isInDropdown && isSuperNav && link.description && (
        <Text
          dataElementPageLocation={isSuperNav ? 'superNav' : 'siteNav'}
          variant={isProductSuite ? 'paragraphS400' : 'interactiveXS400'}
        >
          {link.description}
        </Text>
      )}
    </Link>
  );
};

export { NavLink };
export default NavLink;
